import React, { useMemo } from 'react';
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  type TableColumnSizingOptions,
  type DataGridProps,
  Button,
  Tooltip,
  Text,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';
import {
  CheckmarkRegular,
  DismissRegular,
  CheckmarkCircleFilled,
  DismissCircleFilled,
  ArrowClockwiseDashesRegular,
  ArrowResetRegular,
  DiamondFilled,
} from '@fluentui/react-icons';
import { getTierColor } from '../constants/tiers';
import { formatCurrency, normalizeStatus } from '../utils/formatters';
import { compareString, compareNumber, compareTier } from '../utils/comparators';
import { useFiscalEndReviewPageStyles } from '../styles/FiscalEndReviewPage.styles';
import { usePartnerAliases } from '../contexts/PartnerAliasContext';
import type { PartnerReviewItem } from '../models/partnerReview';

const columnSizingOptions: TableColumnSizingOptions = {
  partnerName:      { minWidth: 140, idealWidth: 170 },
  contractLocation: { minWidth: 120, idealWidth: 140 },
  contractType:     { minWidth: 80,  idealWidth: 100 },
  globalMpnId:      { minWidth: 100, idealWidth: 120 },
  tpId:             { minWidth: 90,  idealWidth: 110 },
  currentTier:      { minWidth: 90,  idealWidth: 110 },
  ttmRevenue:       { minWidth: 100, idealWidth: 120 },
  nextTtm:          { minWidth: 110, idealWidth: 130 },
  futureTtm:        { minWidth: 100, idealWidth: 120 },
  nextTier:         { minWidth: 90,  idealWidth: 110 },
  status:           { minWidth: 90,  idealWidth: 110 },
  notes:            { minWidth: 80,  idealWidth: 90 },
  actions:          { minWidth: 80,  idealWidth: 90 },
};

const tierIconStyle = (tier: string) => ({ color: getTierColor(tier), fontSize: '16px' });

const MemoizedRow = React.memo<{
  item: PartnerReviewItem;
  rowId: string;
  onClickCapture: (e: React.MouseEvent) => void;
  onPartnerClick: (partner: PartnerReviewItem) => void;
  onApprove: (partner: PartnerReviewItem) => void;
  onDeny: (partner: PartnerReviewItem) => void;
  onReset: (partner: PartnerReviewItem) => void;
  isSubmitting: boolean;
  partnerLinkClass: string;
  dashClass: string;
  statusCellClass: string;
  statusIconApprovedClass: string;
  statusIconDeniedClass: string;
  statusIconPendingClass: string;
  actionGroupClass: string;
  actionButtonClass: string;
}>(({ item, rowId, onClickCapture, onPartnerClick, onApprove, onDeny, onReset, isSubmitting, partnerLinkClass, dashClass, statusCellClass, statusIconApprovedClass, statusIconDeniedClass, statusIconPendingClass, actionGroupClass, actionButtonClass }) => {
  const canAct = item.locationMpnId != null;
  const { getDisplayName } = usePartnerAliases();

  const renderTier = (tier: string | null) => {
    if (!tier) return <span className={dashClass}>—</span>;
    return (
      <span className={statusCellClass}>
        <DiamondFilled style={tierIconStyle(tier)} />
        {tier}
      </span>
    );
  };

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'partnerName':
        return (
          <span
            className={partnerLinkClass}
            data-partner-link
            onClickCapture={(e) => { e.stopPropagation(); onPartnerClick(item); }}
          >
            {getDisplayName(item.partnerName) ?? '---'}
          </span>
        );
      case 'contractLocation':
        return item.contractLocation ?? '—';
      case 'contractType':
        return item.contractType ?? '—';
      case 'globalMpnId':
        return item.globalMpnId ?? '—';
      case 'tpId':
        return item.tpId;
      case 'currentTier':
        return renderTier(item.currentTier);
      case 'ttmRevenue':
        return formatCurrency(item.ttmRevenue);
      case 'nextTtm':
        return formatCurrency(item.nextTtm);
      case 'futureTtm':
        return formatCurrency(item.futureTtm);
      case 'nextTier':
        return renderTier(item.nextTier);
      case 'status': {
        const status = normalizeStatus(item.change?.status);
        const iconClass = status === 'Approved' ? statusIconApprovedClass
          : status === 'Denied' ? statusIconDeniedClass
          : statusIconPendingClass;
        const icon = status === 'Approved'
          ? <CheckmarkCircleFilled className={iconClass} />
          : status === 'Denied'
            ? <DismissCircleFilled className={iconClass} />
            : <ArrowClockwiseDashesRegular className={iconClass} />;
        return (
          <span className={statusCellClass}>
            {icon}
            {status}
          </span>
        );
      }
      case 'notes': {
        // Denied reviews always offer the link: denying deletes a prefix-only note
        // server-side, so hasNotes goes false even though a rationale is still wanted.
        const status = normalizeStatus(item.change?.status);
        if (!item.hasNotes && status !== 'Denied') return null;
        return <span className={partnerLinkClass} data-partner-link onClickCapture={(e) => { e.stopPropagation(); onPartnerClick(item); }}>{item.hasNotes ? 'View note' : 'Add note'}</span>;
      }
      case 'actions': {
        const status = normalizeStatus(item.change?.status);
        if (status === 'Approved' || status === 'Denied') {
          return (
            <span onClick={(e) => e.stopPropagation()}>
              <Tooltip content="Undo" relationship="label">
                <Button appearance="subtle" icon={<ArrowResetRegular />} size="small" className={actionButtonClass} disabled={!canAct || isSubmitting} onClick={() => onReset(item)} data-action-button />
              </Tooltip>
            </span>
          );
        }
        return (
          <span className={actionGroupClass} onClick={(e) => e.stopPropagation()}>
            <Tooltip content="Approve" relationship="label">
              <Button appearance="subtle" icon={<CheckmarkRegular />} size="small" className={actionButtonClass} disabled={!canAct || isSubmitting} onClick={() => onApprove(item)} data-action-button />
            </Tooltip>
            <Tooltip content="Deny" relationship="label">
              <Button appearance="subtle" icon={<DismissRegular />} size="small" className={actionButtonClass} disabled={!canAct || isSubmitting} onClick={() => onDeny(item)} data-action-button />
            </Tooltip>
          </span>
        );
      }
      default:
        return null;
    }
  };

  return (
    <DataGridRow<PartnerReviewItem>
      key={rowId}
      selectionCell={{ checkboxIndicator: { 'aria-label': 'Select row' } }}
      onClickCapture={onClickCapture}
    >
      {({ columnId }) => (
        <DataGridCell>
          <TableCellLayout>{renderCell(columnId as string)}</TableCellLayout>
        </DataGridCell>
      )}
    </DataGridRow>
  );
}, (prev, next) => prev.item === next.item && prev.rowId === next.rowId && prev.isSubmitting === next.isSubmitting);

interface PartnerReviewGridProps {
  isLoading: boolean;
  paginatedPartners: PartnerReviewItem[];
  tierChangeLabel: string;
  submittingMpnIds: Set<string>;
  onSelectionChange: NonNullable<DataGridProps['onSelectionChange']>;
  onRowClickCapture: (e: React.MouseEvent) => void;
  onPartnerClick: (partner: PartnerReviewItem) => void;
  onApprove: (partner: PartnerReviewItem) => void;
  onDeny: (partner: PartnerReviewItem) => void;
  onReset: (partner: PartnerReviewItem) => void;
  gridSelectionKey: number;
  defaultSortState: DataGridProps['defaultSortState'];
  onSortChange: (sortColumn: string, sortDirection: 'ascending' | 'descending') => void;
}

export const PartnerReviewGrid: React.FC<PartnerReviewGridProps> = ({
  isLoading,
  paginatedPartners,
  tierChangeLabel,
  submittingMpnIds,
  onSelectionChange,
  onRowClickCapture,
  onPartnerClick,
  onApprove,
  onDeny,
  onReset,
  gridSelectionKey,
  defaultSortState,
  onSortChange,
}) => {
  const styles = useFiscalEndReviewPageStyles();

  const columns: TableColumnDefinition<PartnerReviewItem>[] = useMemo(() => [
    createTableColumn<PartnerReviewItem>({ columnId: 'partnerName', compare: (a, b) => compareString(a.partnerName, b.partnerName), renderHeaderCell: () => 'Partner name', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'contractLocation', compare: (a, b) => compareString(a.contractLocation, b.contractLocation), renderHeaderCell: () => 'Contract location', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'contractType', compare: (a, b) => compareString(a.contractType, b.contractType), renderHeaderCell: () => 'Contract status', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'globalMpnId', compare: (a, b) => compareString(a.globalMpnId, b.globalMpnId), renderHeaderCell: () => 'Global MPN ID', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'tpId', compare: (a, b) => compareNumber(a.tpId, b.tpId), renderHeaderCell: () => 'TPID', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'currentTier', compare: (a, b) => compareTier(a.currentTier, b.currentTier), renderHeaderCell: () => 'Current tier', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'ttmRevenue', compare: (a, b) => compareNumber(a.ttmRevenue, b.ttmRevenue), renderHeaderCell: () => 'TTM revenue', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'nextTtm', compare: (a, b) => compareNumber(a.nextTtm, b.nextTtm), renderHeaderCell: () => 'Next TTM (for Ops)', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'futureTtm', compare: (a, b) => compareNumber(a.futureTtm, b.futureTtm), renderHeaderCell: () => 'Future TTM', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'nextTier', compare: (a, b) => compareTier(a.nextTier, b.nextTier), renderHeaderCell: () => tierChangeLabel, renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'status', compare: (a, b) => compareString(normalizeStatus(a.change?.status), normalizeStatus(b.change?.status)), renderHeaderCell: () => 'Status', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'notes', renderHeaderCell: () => 'Notes', renderCell: () => null }),
    createTableColumn<PartnerReviewItem>({ columnId: 'actions', renderHeaderCell: () => 'Actions', renderCell: () => null }),
  ], [tierChangeLabel]);

  if (isLoading) {
    return (
      <Skeleton>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
          {Array.from({ length: 8 }, (_, i) => (
            <SkeletonItem key={`h-${i}`} style={{ width: `${80 + (i % 3) * 20}px`, height: '14px' }} />
          ))}
        </div>
        {Array.from({ length: 8 }, (_, row) => (
          <div key={`r-${row}`} style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            {Array.from({ length: 8 }, (_, col) => (
              <SkeletonItem key={`c-${col}`} style={{ width: `${80 + (col % 3) * 20}px`, height: '14px' }} />
            ))}
          </div>
        ))}
      </Skeleton>
    );
  }

  if (paginatedPartners.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text size={300}>No partners found matching your current filters.</Text>
      </div>
    );
  }

  return (
    <DataGrid
      key={gridSelectionKey}
      items={paginatedPartners}
      columns={columns}
      sortable
      selectionMode="multiselect"
      onSelectionChange={onSelectionChange}
      getRowId={(item) => `${item.locationMpnId ?? 'null'}-${item.tpId}`}
      resizableColumns
      resizableColumnsOptions={{ autoFitColumns: false }}
      columnSizingOptions={columnSizingOptions}
      defaultSortState={defaultSortState}
      onSortChange={(_e, nextSortState) => {
        onSortChange(nextSortState.sortColumn as string, nextSortState.sortDirection as 'ascending' | 'descending');
      }}
      size="medium"
    >
      <DataGridHeader className={styles.tableHeader}>
        <DataGridRow selectionCell={{ checkboxIndicator: { 'aria-label': 'Select all rows' } }}>
          {({ renderHeaderCell }) => (
            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
          )}
        </DataGridRow>
      </DataGridHeader>
      <DataGridBody<PartnerReviewItem>>
        {({ item, rowId }) => (
          <MemoizedRow
            item={item}
            rowId={rowId as string}
            onClickCapture={onRowClickCapture}
            onPartnerClick={onPartnerClick}
            onApprove={onApprove}
            onDeny={onDeny}
            onReset={onReset}
            isSubmitting={item.locationMpnId != null && submittingMpnIds.has(item.locationMpnId)}
            partnerLinkClass={styles.partnerLink}
            dashClass={styles.dash}
            statusCellClass={styles.statusCell}
            statusIconApprovedClass={styles.statusIconApproved}
            statusIconDeniedClass={styles.statusIconDenied}
            statusIconPendingClass={styles.statusIconPending}
            actionGroupClass={styles.actionGroup}
            actionButtonClass={styles.actionButton}
          />
        )}
      </DataGridBody>
    </DataGrid>
  );
};
